/**
 * Per-market branding and defaults.
 *
 * The product is sold under a different name in each market, and each market opens on a different
 * symbol, so both follow the customer's market rather than the deployment. Market ids are the ones
 * the backend's `Market` enum defines and that `user.marketId` carries: 1 = US/International,
 * 2 = India.
 *
 * Kept in one place because the watermark alone was set from three (the ECharts render, the admin
 * small-watermark toggle, and the chart theme default), and the default ticker from another.
 * Every extra copy is another chance to miss one when a market is added.
 */

/** Watermark text by market id. */
export const WATERMARK_BY_MARKET_ID = {
  1: 'AstroStockCharts',
  2: 'Dhanabhyas',
};

/** Symbol each market opens on, matching Market.defaultSymbol in the backend. */
export const DEFAULT_TICKER_BY_MARKET_ID = {
  1: 'SPY',
  2: 'NSEI',
};

/**
 * Fallback when the market is unknown — an anonymous visitor, or the moment before the profile has
 * loaded. The US brand is the safe default: it is the deployment's own name, so showing it briefly
 * is not a mislabel the way showing the India brand to a US customer would be.
 */
export const DEFAULT_WATERMARK = WATERMARK_BY_MARKET_ID[1];

/** The market ids the product actually supports. */
export const KNOWN_MARKET_IDS = Object.keys(WATERMARK_BY_MARKET_ID).map(Number);

/**
 * Normalises whatever form of "market" is to hand into an id.
 *
 * Accepts the number, its string form, or the market code. `/api/user/me` returns BOTH
 * `marketId: 2` and `market: "IN"` — the latter is a derived view the backend serialises — so
 * taking either means a profile that somehow carries only one of them still resolves correctly.
 */
export function toMarketId(market) {
  if (market === null || market === undefined || market === '') return null;

  const s = String(market).trim();
  if (/^\d+$/.test(s)) {
    // Validated against the markets we know rather than returned as-is. An id of 99 is not a
    // market, and answering "99" would push the decision onto every caller — one of which would
    // eventually forget to check and brand a chart from an undefined lookup.
    const n = Number(s);
    return KNOWN_MARKET_IDS.includes(n) ? n : null;
  }

  const code = s.toUpperCase();
  if (code === 'IN' || code === 'IND' || code === 'INDIA') return 2;
  if (code === 'US' || code === 'INTERNATIONAL') return 1;
  return null;
}

/** Watermark text for a market id, code, or name. */
export function watermarkForMarket(market) {
  const id = toMarketId(market);
  return (id !== null && WATERMARK_BY_MARKET_ID[id]) || DEFAULT_WATERMARK;
}

/**
 * The symbol a market should open on, or null when the market is unknown.
 *
 * Null rather than a US default on purpose: the caller falls back to the first symbol of whichever
 * list actually loaded, which is always a valid choice, whereas insisting on SPY for an unknown
 * market would select a ticker that may not be in the customer's list at all.
 */
export function defaultTickerForMarket(market) {
  const id = toMarketId(market);
  return (id !== null && DEFAULT_TICKER_BY_MARKET_ID[id]) || null;
}
